import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from '../../i18n';

import { Card } from '../../components/Shared/Card';
import { Button } from '../../components/Shared/Button';
import { FormField } from '../../components/Shared/FormField';
import { ContactPersonFields } from '../../components/Contacts/ContactPersonFields';
import { SnippetTextArea } from '../../components/Shared/SnippetTextArea';
import SearchSelect from '../../components/Shared/SearchSelect';

import { useOfferStore } from '../../store/useOfferStore';
import { useCustomerStore } from '../../store/useCustomerStore';
import { useObjectStore } from '../../store/useObjectStore';
import { useTextSnippetStore } from '../../store/useTextSnippetStore';
import type { OfferSnippetContext } from '../../components/Shared/SnippetTextArea';
import { mapApiFieldErrors, hasFieldErrors } from '../../errors';
import { isoDate } from '../../utils/dateUtils';
import type { Salutation } from '../../utils/salutation';

function getTodayDate(): string {
  return isoDate(new Date());
}

function getThreeMonthsLater(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 3);
  return isoDate(d);
}

export default function OfferCreatePage() {
  const { t } = useTranslation(['offers', 'common']);
  const navigate = useNavigate();

  const { createOffer } = useOfferStore();
  const { customers, fetchCustomers } = useCustomerStore();
  const { objects, loading: objectsLoading, fetchAllObjects, getObjectById } = useObjectStore();
  const [searchParams] = useSearchParams();
  const presetObjectId = searchParams.get('objectId');
  // Object to select once the customer's objects are loaded (FACF-68: "new offer" on an object).
  const pendingObjectId = useRef<string | null>(null);
  const fetchSnippets = useTextSnippetStore((s) => s.fetchSnippets);

  const [customerId, setCustomerId] = useState('');
  const [objectId, setObjectId] = useState('');
  const [contactSalutation, setContactSalutation] = useState<Salutation | null>(null);
  const [contactPersonName, setContactPersonName] = useState('');
  const [offerDate, setOfferDate] = useState(getTodayDate());
  const [validUntilDate, setValidUntilDate] = useState(
    getThreeMonthsLater()
  );
  const [taxRate, setTaxRate] = useState('20');
  const [introductionText, setIntroductionText] = useState('');
  const [closingText, setClosingText] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCustomers({ pageSize: 200 });
    fetchSnippets({ isActive: true });
  }, [fetchCustomers, fetchSnippets]);

  useEffect(() => {
    if (!presetObjectId) return;
    getObjectById(presetObjectId).then((preset) => {
      if (preset?.id && preset.customerId) {
        pendingObjectId.current = preset.id;
        setCustomerId(preset.customerId);
      }
    });
  }, [presetObjectId, getObjectById]);

  useEffect(() => {
    if (customerId) {
      // why: fetchAllObjects ignores the object list's paging/search state, which used to
      // leave the picker empty for customers whose objects were not on that page (FACF-68).
      fetchAllObjects({ customerId });
      setObjectId(pendingObjectId.current ?? '');
      pendingObjectId.current = null;
    }
  }, [customerId, fetchAllObjects]);

  const customerOptions = useMemo(
    () =>
      customers.map((c) => ({
        id: c.id ?? '',
        label: c.name ?? '',
      })),
    [customers]
  );

  const objectOptions = useMemo(
    () =>
      objects
        // Guards against a slower response for a previously selected customer.
        .filter((o) => o.customerId === customerId)
        .map((o) => ({
          id: o.id ?? '',
          label: o.name ?? '',
        })),
    [objects, customerId]
  );

  const renderContext: OfferSnippetContext = useMemo(() => {
    const tax = parseFloat(taxRate);
    return {
      customerId: customerId || null,
      objectId: objectId || null,
      contactSalutation,
      contactPersonName: contactPersonName || null,
      offerDate: offerDate || null,
      validUntilDate: validUntilDate || null,
      taxRate: Number.isFinite(tax) ? tax : null,
    };
  }, [customerId, objectId, contactSalutation, contactPersonName, offerDate, validUntilDate, taxRate]);

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {};
    if (!customerId) {
      newErrors.customerId = t('offers:validation.customerRequired');
    }
    if (!objectId) {
      newErrors.objectId = t('offers:validation.objectRequired');
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      const id = await createOffer({
        customerId,
        objectId,
        // why: contactPersonName is required by CreateOfferRequest — send '' rather than null.
        contactPersonName: contactPersonName || '',
        contactSalutation,
        introductionText: introductionText || null,
        closingText: closingText || null,
        offerDate,
        validUntilDate,
        taxRate: parseFloat(taxRate) || 20,
        paymentTerms: paymentTerms || null,
      });
      if (id) {
        navigate(`/offers/${id}`);
      }
    } catch (err) {
      // Surface per-field validation errors from the API contract.
      if (hasFieldErrors(err)) {
        setErrors(mapApiFieldErrors(err));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/offers')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common:actions.back')}
        </Button>
        <h1 className="text-2xl font-semibold">
          {t('offers:create.title')}
        </h1>
      </div>

      <Card>
        <div className="p-6 space-y-6">
          <CustomerAndObjectSection
            customerId={customerId}
            objectId={objectId}
            onCustomerChange={setCustomerId}
            onObjectChange={setObjectId}
            customerOptions={customerOptions}
            objectOptions={objectOptions}
            objectsLoading={objectsLoading}
            errors={errors}
            t={t}
          />

          <ContactPersonFields
            customerId={customerId}
            salutation={contactSalutation}
            name={contactPersonName}
            onChange={({ salutation, name }) => {
              setContactSalutation(salutation);
              setContactPersonName(name);
            }}
            // why: the object's responsible contact drives the auto-pick (FACF-102);
            // ContactPersonFields falls back to the customer's first contact without one.
            preferredContactId={
              objects.find((o) => o.id === objectId)?.responsibleContactId ?? null
            }
            nameLabel={t('offers:fields.contactPersonName')}
            namePlaceholder={t('offers:placeholders.contactPersonName')}
            nameError={errors.contactPersonName}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              label={t('offers:fields.offerDate')}
              name="offerDate"
              type="date"
              value={offerDate}
              onChange={(e) => setOfferDate(e.target.value)}
              required
            />
            <FormField
              label={t('offers:fields.validUntilDate')}
              name="validUntilDate"
              type="date"
              value={validUntilDate}
              onChange={(e) => setValidUntilDate(e.target.value)}
              required
            />
            <FormField
              label={t('offers:fields.taxRate')}
              name="taxRate"
              type="number"
              value={taxRate}
              onChange={(e) => setTaxRate(e.target.value)}
              required
            />
          </div>

          <SnippetTextArea
            label={t('offers:fields.introductionText')}
            name="introductionText"
            value={introductionText}
            onChange={setIntroductionText}
            category="OfferIntroduction"
            documentType="offer"
            rows={3}
            placeholder={t('offers:placeholders.introductionText')}
            renderContext={renderContext}
          />

          <SnippetTextArea
            label={t('offers:fields.closingText')}
            name="closingText"
            value={closingText}
            onChange={setClosingText}
            category="OfferClosingText"
            documentType="offer"
            rows={3}
            placeholder={t('offers:placeholders.closingText')}
            renderContext={renderContext}
          />

          <SnippetTextArea
            label={t('offers:fields.paymentTerms')}
            name="paymentTerms"
            value={paymentTerms}
            onChange={setPaymentTerms}
            category="OfferPaymentTerms"
            documentType="offer"
            rows={2}
            placeholder={t('offers:placeholders.paymentTerms')}
            renderContext={renderContext}
          />

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="secondary"
              onClick={() => navigate('/offers')}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving
                ? t('common:actions.saving')
                : t('offers:actions.create')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

interface CustomerAndObjectSectionProps {
  customerId: string;
  objectId: string;
  onCustomerChange: (id: string) => void;
  onObjectChange: (id: string) => void;
  customerOptions: { id: string; label: string }[];
  objectOptions: { id: string; label: string }[];
  objectsLoading: boolean;
  errors: Record<string, string>;
  t: (key: string) => string;
}

function CustomerAndObjectSection({
  customerId,
  objectId,
  onCustomerChange,
  onObjectChange,
  customerOptions,
  objectOptions,
  objectsLoading,
  errors,
  t,
}: CustomerAndObjectSectionProps) {
  return (
    <div className="space-y-4">
      <div>
        <SearchSelect
          options={customerOptions}
          value={customerId}
          onChange={onCustomerChange}
          label={t('offers:fields.customer')}
          required
          placeholder={t('offers:placeholders.customer')}
          noResultsText={t('offers:placeholders.noCustomers')}
        />
        {errors.customerId && (
          <p className="mt-1 text-sm text-red-600">
            {errors.customerId}
          </p>
        )}
      </div>

      <div>
        {customerId ? (
          <SearchSelect
            options={objectOptions}
            value={objectId}
            onChange={onObjectChange}
            label={t('offers:fields.object')}
            required
            placeholder={t('offers:placeholders.object')}
            noResultsText={
              objectsLoading ? t('offers:placeholders.loadingObjects') : t('offers:placeholders.noObjects')
            }
          />
        ) : (
          <FormField
            label={t('offers:fields.object')}
            name="objectId"
            value=""
            disabled
            required
            placeholder={t('offers:placeholders.selectCustomerFirst')}
          />
        )}
        {errors.objectId && (
          <p className="mt-1 text-sm text-red-600">
            {errors.objectId}
          </p>
        )}
      </div>
    </div>
  );
}
